import styled, { css, keyframes } from 'styled-components';
import ndsTokens from 'nicollet-tokens';
import { ndsFontReset, ndsFormStyles } from '@nicollet/common';
import { Label } from '../Label/index.js';
import { Microcopy } from '../Microcopy/index.js';

const RADIO_SIZE = 20;
const RADIO_FILLED_SIZE = 14;
const labelLeftPadding = RADIO_SIZE + 8;
const RadioOuterWrap = styled.div`
  ${ndsFontReset()};
  box-sizing: border-box;
  line-height: 0;
  position: relative;
`;
const radioFocusStyles = css`
  outline: ${ndsTokens.colors.text.formLabel} dashed 1px;
  outline-offset: 2px;
  -moz-outline-radius: 4px;
`;
const RadioInput = styled.input`
  border: 0;
  cursor: pointer;
  height: ${RADIO_SIZE}px;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: ${RADIO_SIZE}px;
`;
const RadioMicrocopy = styled(Microcopy)`
  padding-left: ${labelLeftPadding}px;
  padding-top: 0;
  width: 100%;
  ${({
  microcopyPlacement
}) => microcopyPlacement === 'right' && `
    width: auto;
  `}
`;
const RadioWrap = styled.label`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  position: relative;
  width: 100%;

  ${({
  microcopyPlacement
}) => microcopyPlacement === 'right' && `
    justify-content: space-between;
    flex-wrap: nowrap;`}
  ${props => props.hasFocus && radioFocusStyles};
  ${RadioInput}:focus + & {
    ${radioFocusStyles};
  }
`;
/* Radio Label - the text to the right of the radio button */

const radioLabelHoverStyles = css`
  color: #111; /* Hard coding here cause there's no neutral token darker than #333333 */
`;
const radioLabelActiveStyles = css`
  color: ${ndsTokens.colors.palette.gray.darkest};
`;
const RadioLabel = styled(Label)`
  padding-left: ${labelLeftPadding}px;

  ${props => props.hidden && `
    padding-left: 0;
  `};

  ${props => props.hasHover && radioLabelHoverStyles};
  ${RadioWrap}:hover & {
    ${radioLabelHoverStyles};
  }

  ${props => props.hasActive && radioLabelActiveStyles};
  ${RadioInput}:active + ${RadioWrap} & {
    ${radioLabelActiveStyles};
  }

  ${RadioInput}:disabled + ${RadioWrap} & {
    color: ${ndsTokens.colors.text.onLight.secondary};
    background: transparent;
  }
`;
/* Radio Visual - the actual "radio button" you see */

const radioVisualBaseStyles = css`
  background-color: ${ndsTokens.colors.background.default};
  border-radius: 50%;
  position: absolute;
  box-sizing: border-box;
  background-clip: padding-box;
  height: ${RADIO_SIZE}px;
  width: ${RADIO_SIZE}px;
`;
const radioVisualHoverStyles = css`
  &::before {
    border-color: ${ndsTokens.colors.border.interactive.secondary.hover};
  }
`;
const radioVisualCheckedHoverStyles = css`
  &::before {
    border-color: ${ndsTokens.colors.palette.green.default};
  }

  &::after {
    background-color: ${ndsTokens.colors.palette.green.default};
  }
`;
const radioVisualActiveStyles = css`
  &::before {
    background-color: ${ndsTokens.colors.palette.gray.light};
    border-color: ${ndsTokens.colors.border.control.input};
  }
`;
const radioVisualCheckedActiveStyles = css`
  &::before {
    background-color: ${ndsTokens.colors.background.default};
    border-color: ${ndsTokens.colors.palette.green.light};
  }

  &::after {
    background-color: ${ndsTokens.colors.palette.green.light};
  }
`;
const radioFillCircleStyles = css`
  background-clip: padding-box;
  box-sizing: border-box;
  border-radius: 50%;
  border: 0;
  content: '';
  display: flex;
  height: ${RADIO_FILLED_SIZE}px;
  opacity: 0;
  position: relative;
  transform: scale(0.6);
  width: ${RADIO_FILLED_SIZE}px;
  align-self: center;
  transition: background-color 150ms ease-in-out;
`;
const logoCheck = keyframes`
  0% {
    opacity: 0;
    transform: translateZ(0) scale(.52);
  }

  100% {
    opacity: 1;
    transform: translateZ(0) scale(1);
  }
`;
const logoCheckBorder = keyframes`
  0% {
    opacity: 1;
    transform: translateZ(0) scale(1);
    border-color: ${ndsTokens.colors.border.control.input};
  }

  50% {
    opacity: 0.5;
    transform: translateZ(0) scale(.667);
    border-color: ${ndsTokens.colors.palette.green.light};
  }

  100% {
    opacity: 1;
    transform: translateZ(0) scale(1);
    border-color: ${ndsTokens.colors.palette.green.light};
  }
`;
const RadioVisual = styled.div`
  display: flex;
  place-content: center;
  top: 1px;
  ${radioVisualBaseStyles}

  &::before {
    border: 1px solid ${ndsTokens.colors.border.control.input};
    opacity: 1;
    content: '';
    transition: border-color 150ms ease-in-out;
    ${radioVisualBaseStyles}
  }

  &::after {
    background-color: ${ndsTokens.colors.palette.green.light};
    ${radioFillCircleStyles};
  }

  /* If label is hidden, radio should be statically positioned so focus ring works correctly */
  ${props => props.hiddenLabel && `position: static;`}

  /* If invalid, use error state colors on radio */
  ${props => props.valid === false && `
      &::before {
        background-color: ${ndsFormStyles.colors.alert.warning.background};
        border-color: ${ndsFormStyles.colors.alert.warning.text};
      }
    `}

  /* If valid, use success state colors on radio */
  ${props => props.valid === true && `
      &::before {
        border-color: ${ndsTokens.colors.alert.success};
      }
    `}

  /* hover */
  ${props => props.hasHover && radioVisualHoverStyles}
  ${RadioWrap}:hover & {
    ${radioVisualHoverStyles}
  }

  /* Checked */
  ${RadioInput}:checked + ${RadioWrap} & {
    &::before {
      background-color: ${ndsTokens.colors.background.default};
      border-color: ${ndsTokens.colors.palette.green.light};
      animation: ${logoCheckBorder} 250ms cubic-bezier(0.9, 0.885, 0.32, 1.6);
      ${({
  noAnimation
}) => noAnimation ? `animation-duration: 0ms;` : ''}

      @media (prefers-reduced-motion: reduce) {
        animation-duration: 0ms;
      }
    }

    &::after {
      transform: translateZ(0) scale(1); /* translateZ(0px) fixes some artifacting in Safari */
      animation: ${logoCheck} 120ms ease-out 40ms forwards;
      ${({
  noAnimation
}) => noAnimation ? `
            animation-duration: 0ms;
            animation-delay: 0ms;
          ` : ''}

      @media (prefers-reduced-motion: reduce) {
        animation-duration: 0ms;
        animation-delay: 0ms;
      }
    }

    ${props => props.hasHover && radioVisualCheckedHoverStyles};
    ${props => props.hasActive && radioVisualCheckedActiveStyles};
  }

  /* Checked, hover */
  ${RadioInput}:checked:hover + ${RadioWrap} & {
    ${radioVisualCheckedHoverStyles}
  }

  /* Checked, active */
  ${RadioInput}:checked:active + ${RadioWrap} & {
    ${radioVisualCheckedActiveStyles}
  }

  /* Active */
  ${props => props.hasActive && radioVisualActiveStyles}
  ${RadioInput}:active + ${RadioWrap} & {
    ${radioVisualActiveStyles}
  }

  /* Disabled */
  ${RadioInput}:disabled + ${RadioWrap} & {
    display: grid;
    place-content: center;
    cursor: not-allowed;
    filter: none; /* counteract :active styling */
    pointer-events: none;
    background-color: ${ndsTokens.colors.background.default};
    border: 1px solid ${ndsTokens.colors.palette.gray.medium};

    &::before {
      content: '';
      position: relative;
      left: 0;
      width: 16px;
      height: 16px;
      border: 2px solid ${ndsTokens.colors.palette.gray.medium};
      border-radius: ${ndsTokens.border.radius.circle};
      background: transparent;
    }

    &::after {
      background-color: ${ndsTokens.colors.palette.gray.medium};
      border-radius: 0;
      border: 0;
      content: '';
      height: 2px;
      left: 2px;
      top: calc(50% - 1px);
      margin-top: 0;
      opacity: 1;
      position: absolute;
      transform: translateZ(0) rotate(45deg);
      width: ${RADIO_FILLED_SIZE}px;
    }
  }

  /* Disabled, checked */
  ${RadioInput}:disabled:checked + ${RadioWrap} & {
    background-color: ${ndsTokens.colors.background.default};
    border-color: ${ndsTokens.colors.palette.gray.medium};

    &::before {
      display: none;
    }

    &::after {
      top: 0;
      left: 0;
      ${radioFillCircleStyles};
      background-color: ${ndsTokens.colors.palette.gray.medium};
      opacity: 1;
      transform: translateZ(0) scale(1); /* translateZ(0px) fixes border artifacts in Safari */
    }
  }
`;

export { RadioInput, RadioLabel, RadioMicrocopy, RadioOuterWrap, RadioVisual, RadioWrap };
